/**
 * Vertex360 Copilot conversation CRUD + history (Laravel-owned).
 *
 * Streaming inference goes to the AI service (see vertex360Copilot.services.js);
 * everything here — sidebar list, history pagination, rename/star/delete — is
 * served by Laravel via the shared authenticated apiCall client.
 */
import { apiCall } from "./service";
import {
  URL_SP_AI_VERTEX360_COPILOT_CONVERSATION,
  URL_SP_AI_VERTEX360_COPILOT_CONVERSATIONS,
  URL_SP_AI_VERTEX360_COPILOT_CONVERSATIONS_BULK_DELETE,
  URL_SP_AI_VERTEX360_COPILOT_CONVERSATIONS_RECENT,
  URL_SP_AI_VERTEX360_COPILOT_CONVERSATIONS_STARRED,
  URL_SP_AI_VERTEX360_COPILOT_MESSAGES,
} from "./URL";

const unwrap = (response) => response?.data ?? response;

const conversationPageQuery = ({ page, limit } = {}) => {
  const query = new URLSearchParams();
  if (page) query.set("page", String(page));
  if (limit) query.set("limit", String(limit));
  const suffix = query.toString() ? `?${query.toString()}` : "";
  return suffix;
};

/** Normalize a paginated conversations response from Laravel. */
const normalizeConversationPage = (payload) => {
  const data = payload?.data && !Array.isArray(payload.data) ? payload.data : payload;
  const rawList = data?.conversations;
  const list = Array.isArray(rawList)
    ? rawList
    : Array.isArray(rawList?.data)
      ? rawList.data
      : Array.isArray(data)
        ? data
        : Array.isArray(payload)
          ? payload
          : [];

  return {
    conversations: list,
    has_more: Boolean(data?.has_more),
    page: Number(data?.page) || 1,
    next_page: data?.next_page != null ? Number(data.next_page) : null,
    page_size: Number(data?.page_size) || list.length,
  };
};

export const vertex360CopilotConversationApi = {
  /** @deprecated Prefer listStarredConversations / listRecentConversations. */
  listConversations() {
    return apiCall
      .get({
        url: URL_SP_AI_VERTEX360_COPILOT_CONVERSATIONS,
        isAuthToken: true,
        skipQueryParams: true,
      })
      .then(unwrap);
  },

  listStarredConversations({ page = 1, limit } = {}) {
    return apiCall
      .get({
        url: `${URL_SP_AI_VERTEX360_COPILOT_CONVERSATIONS_STARRED}${conversationPageQuery({ page, limit })}`,
        isAuthToken: true,
        skipQueryParams: true,
      })
      .then(unwrap)
      .then(normalizeConversationPage);
  },

  listRecentConversations({ page = 1, limit } = {}) {
    return apiCall
      .get({
        url: `${URL_SP_AI_VERTEX360_COPILOT_CONVERSATIONS_RECENT}${conversationPageQuery({ page, limit })}`,
        isAuthToken: true,
        skipQueryParams: true,
      })
      .then(unwrap)
      .then(normalizeConversationPage);
  },

  getMessages(uuid, { beforeId, limit } = {}) {
    const query = new URLSearchParams();
    if (beforeId) query.set("before_id", String(beforeId));
    if (limit) query.set("limit", String(limit));
    const suffix = query.toString() ? `?${query.toString()}` : "";

    return apiCall
      .get({
        url: `${URL_SP_AI_VERTEX360_COPILOT_MESSAGES(uuid)}${suffix}`,
        isAuthToken: true,
        skipQueryParams: true,
      })
      .then(unwrap);
  },

  rename(uuid, title) {
    return apiCall
      .patch({
        url: URL_SP_AI_VERTEX360_COPILOT_CONVERSATION(uuid),
        params: { title },
        isAuthToken: true,
      })
      .then(unwrap);
  },

  setStarred(uuid, starred) {
    return apiCall
      .patch({
        url: URL_SP_AI_VERTEX360_COPILOT_CONVERSATION(uuid),
        params: { starred },
        isAuthToken: true,
      })
      .then(unwrap);
  },

  remove(uuid) {
    return this.removeMany([uuid]);
  },

  removeMany(uuids) {
    const ids = (Array.isArray(uuids) ? uuids : []).filter(Boolean);
    if (!ids.length) {
      return Promise.resolve({ deleted: 0, requested: 0 });
    }
    return apiCall
      .post({
        url: URL_SP_AI_VERTEX360_COPILOT_CONVERSATIONS_BULK_DELETE,
        params: { uuids: ids },
        isAuthToken: true,
      })
      .then(unwrap);
  },
};
